import {
  AuthConfigSchema,
  CognitoAuthResponseSchema,
  type AuthConfig,
  type CognitoAuthResponse,
} from '@cer-autofill/shared';
import { z } from 'zod';
import { backendBaseUrl } from './env.js';

export type { AuthConfig } from '@cer-autofill/shared';

export async function fetchAuthConfig(): Promise<AuthConfig> {
  const res = await fetch(`${backendBaseUrl}/api/auth/config`);
  if (!res.ok) throw new Error(`Failed to load auth config (${res.status})`);
  return AuthConfigSchema.parse(await res.json());
}

export async function startEmailOtp(
  config: Extract<AuthConfig, { authEnabled: true }>,
  email: string,
) {
  return cognitoRequest(config.region, 'InitiateAuth', {
    AuthFlow: 'USER_AUTH',
    ClientId: config.clientId,
    AuthParameters: {
      USERNAME: email.trim().toLowerCase(),
      PREFERRED_CHALLENGE: 'EMAIL_OTP',
    },
  });
}

export async function answerEmailOtp(input: {
  config: Extract<AuthConfig, { authEnabled: true }>;
  email: string;
  code: string;
  session: string;
}): Promise<CognitoAuthResponse> {
  return cognitoRequest(input.config.region, 'RespondToAuthChallenge', {
    ChallengeName: 'EMAIL_OTP',
    ClientId: input.config.clientId,
    Session: input.session,
    ChallengeResponses: {
      USERNAME: input.email.trim().toLowerCase(),
      EMAIL_OTP_CODE: input.code.trim(),
    },
  });
}

export async function refreshCognitoTokens(input: {
  refreshToken: string;
}): Promise<NonNullable<CognitoAuthResponse['AuthenticationResult']>> {
  const config = await fetchAuthConfig();
  if (!config.authEnabled) throw new Error('Cognito authentication is not enabled.');
  const response = await cognitoRequest(config.region, 'InitiateAuth', {
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    ClientId: config.clientId,
    AuthParameters: { REFRESH_TOKEN: input.refreshToken },
  });
  if (!response.AuthenticationResult?.IdToken) {
    throw new Error('Cognito did not return refreshed tokens.');
  }
  return response.AuthenticationResult;
}

const FieldSessionProfileSchema = z.object({
  displayName: z.string().min(1),
  email: z.string().email().optional(),
  inspectorId: z.string().min(1).optional(),
  roles: z.array(z.string()),
  source: z.string(),
});
export type FieldSessionProfile = z.infer<typeof FieldSessionProfileSchema>;

export async function fetchFieldSessionProfile(input: {
  idToken: string;
  deviceId: string;
}): Promise<FieldSessionProfile> {
  const res = await fetch(`${backendBaseUrl}/api/field/session`, {
    headers: {
      Authorization: `Bearer ${input.idToken}`,
      'X-Field-Device-Id': input.deviceId,
    },
  });
  if (!res.ok) throw new Error(`Failed to load field profile (${res.status})`);
  return FieldSessionProfileSchema.parse(await res.json());
}

function cognitoRequest(
  region: string,
  target: 'InitiateAuth' | 'RespondToAuthChallenge',
  body: Record<string, unknown>,
): Promise<CognitoAuthResponse> {
  return fetch(`https://cognito-idp.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  }).then(async (res) => {
    const json: unknown = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message =
        typeof json === 'object' && json && '__type' in json
          ? String((json as Record<string, unknown>)['__type'])
          : `Cognito ${target} failed`;
      throw new Error(message);
    }
    return CognitoAuthResponseSchema.parse(json);
  });
}

export function displayNameFromIdToken(idToken: string, fallbackEmail: string): string {
  try {
    const [, payload] = idToken.split('.');
    if (!payload) return fallbackEmail;
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
      name?: string;
      email?: string;
    };
    return decoded.name || decoded.email || fallbackEmail;
  } catch {
    return fallbackEmail;
  }
}
